/**
 * Monetisation intent-experiment tracking + persistence.
 *
 * Three event names are defined by the experiment:
 *   - `export_attempt` : fires whenever a user triggers any export action.
 *   - `gate_seen`      : fires when the intent form becomes visible.
 *   - `intent_captured`: fires when the user submits the intent form (carries
 *                        only the non-identifying segment + willingness-to-pay
 *                        dimensions — never the email, which is PII).
 *
 * The submitted email/segment/willingness record is persisted to localStorage
 * so the experiment has a local sink without adding any server-side dependency
 * (the SPA remains the single canonical frontend; see architecture invariant
 * fx-inv-architecture-no-server-side-deps).
 */

import { trackEvent } from './googleAnalytics';
import type {
  CalculationContextSnapshot,
  IntentRecord,
  IntentSegment,
  MonetisationLeadRecord,
  WillingnessToPay,
} from '../../types/monetisation';

const STORAGE_KEY = 'rt:export-intent';
const LEAD_RECORD_STORAGE_KEY = 'rt:monetisation-lead';

export function trackExportAttempt(source: string): void {
  trackEvent({
    event_name: 'export_attempt',
    event_category: 'monetisation',
    event_label: source,
    custom_parameters: { export_source: source },
  });
}

export function trackGateSeen(source: string): void {
  trackEvent({
    event_name: 'gate_seen',
    event_category: 'monetisation',
    event_label: source,
    custom_parameters: { gate_source: source },
  });
}

/**
 * Fires when the user successfully submits the intent gate (AC fx-2.2#AC4).
 * Marks that the gate was submitted (and the export therefore unlocked) for a
 * given trigger source.
 */
export function trackGateSubmit(source: string): void {
  trackEvent({
    event_name: 'gate_submit',
    event_category: 'monetisation',
    event_label: source,
    custom_parameters: { gate_source: source },
  });
}

/**
 * Fires the `wtp_response` event carrying the willingness-to-pay answer (and
 * the non-identifying segment dimension) on a successful submit
 * (AC fx-2.2#AC4). Never carries the email (PII).
 */
export function trackWtpResponse(input: {
  segment: IntentSegment;
  willingnessToPay: WillingnessToPay;
}): void {
  trackEvent({
    event_name: 'wtp_response',
    event_category: 'monetisation',
    custom_parameters: {
      segment: input.segment,
      willingness_to_pay: input.willingnessToPay,
    },
  });
}

export function trackIntentCaptured(input: {
  segment: IntentSegment;
  willingnessToPay: WillingnessToPay;
  hasEmail: boolean;
}): void {
  trackEvent({
    event_name: 'intent_captured',
    event_category: 'monetisation',
    custom_parameters: {
      segment: input.segment,
      willingness_to_pay: input.willingnessToPay,
      provided_email: input.hasEmail,
    },
  });
}

export function saveIntentRecord(record: IntentRecord): void {
  if (typeof window === 'undefined') return;
  try {
    const withTimestamp: IntentRecord = {
      ...record,
      capturedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(withTimestamp));
  } catch {
    // localStorage may be unavailable (private mode / quota) — fail silently.
  }
}

/**
 * Persists the full monetisation lead record — email, segment, WTP response
 * and the triggering calculation context — to localStorage (AC fx-2.2#AC2).
 * This is the local sink; the same record is also written to the lead-capture
 * path (ERPNext / mailto) by the gate.
 */
export function saveMonetisationLeadRecord(record: MonetisationLeadRecord): void {
  if (typeof window === 'undefined') return;
  try {
    const withTimestamp: MonetisationLeadRecord = {
      ...record,
      capturedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(LEAD_RECORD_STORAGE_KEY, JSON.stringify(withTimestamp));
  } catch {
    // localStorage may be unavailable (private mode / quota) — fail silently.
  }
}

export function readMonetisationLeadRecord(): MonetisationLeadRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LEAD_RECORD_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as MonetisationLeadRecord;
  } catch {
    return null;
  }
}

export function clearMonetisationLeadRecord(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(LEAD_RECORD_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export type { CalculationContextSnapshot };

export function readIntentRecord(): IntentRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as IntentRecord;
  } catch {
    return null;
  }
}

export function clearIntentRecord(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
